import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsMobile } from '@/app/useIsMobile';
import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { NewTenantDialog } from '@/features/provisioning/NewTenantDialog';
import { ProvisionWizard } from '@/features/provisioning/ProvisionWizard';
import {
  startImpersonation,
  useTenants,
  type AdminTenant,
} from '@/features/tenants/useTenants';
import { ResetPasswordDialog } from '@/features/users/ResetPasswordDialog';
import { EditLoginDialog } from '@/features/users/EditLoginDialog';
import { CommandPinResetDialog } from '@/features/users/CommandPinResetDialog';
import { RaadSecureDialog } from '@/features/tenants/RaadSecureDialog';
import { EditAccountNameDialog } from '@/features/tenants/EditAccountNameDialog';
import { CloseAccountDialog } from '@/features/tenants/CloseAccountDialog';
import { ReopenDialog } from '@/features/tenants/ReopenDialog';
import { ReleaseFleetDialog } from '@/features/tenants/ReleaseFleetDialog';

/**
 * Tenants — canvas `1t` (slices 17b + 17c).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT MATCHES THE ARTBOARD, AND WHAT CANNOT.
 * ─────────────────────────────────────────────────────────────────────────────
 * Matching: the header count line (`N active · N vehicles`), the search box, the
 * vehicle-count badge beside the tenant name, the Plan / MRR / Status columns,
 * and an Actions column whose **Open portal** is real as of 17c.
 *
 * NOT matching, and each is a missing FACT rather than missing styling:
 *
 *   - **City** and **Type** — no such columns on `tenants`. They could be added
 *     in a migration, but there is no tenant-edit surface anywhere, so they
 *     would render empty for every tenant indefinitely. A column that is always
 *     blank makes the screen look more finished and be less true. They arrive
 *     with somewhere to enter them.
 *   - **Invoices** — the table exists and holds ZERO rows (Phase 3B). A button
 *     opening an empty list is a control for a feature that is not there.
 *   - **+ New tenant** landed in **slice 18b** and is in the header. Adding a
 *     VEHICLE to an existing tenant landed in slice 18 and is the second button
 *     in the Actions column. Both appear because there is now somewhere to POST
 *     them; until slice 18b the pilot's tenant had been created by hand in SQL,
 *     which is the "zero manual DB edits" gate slice 18 claimed and did not
 *     actually close.
 *
 * Money renders here and nowhere else: `monthlyRateCents` is integer cents on
 * the wire and is divided exactly once, at this edge.
 */

/** Cents → `$6.00`. NULL is a dash, never `$0.00` — see the note in the body. */
function Money({ cents }: { cents: number | null }) {
  const { t } = useTranslation();
  if (cents === null) {
    // 🔴 NULL IS NOT ZERO. Raad Internal has no rate because it is not billed,
    // which is a different statement from "this customer pays nothing".
    return <span className="text-raad-muted">{t('tenants.noRate')}</span>;
  }
  return <span className="num">{`$${(cents / 100).toFixed(2)}`}</span>;
}

const STATUS_TONE: Record<AdminTenant['status'], string> = {
  active: 'border-raad-border text-raad-muted',
  past_due: 'border-raad-warn/40 text-raad-warn',
  suspended: 'border-raad-error/40 text-raad-error',
};

/**
 * Slice 11b — the read-only offboarding + detection-pause line under a tenant's
 * status. NO retention promise and NO delete control of any kind: it states how
 * long an account has been closed and where the reopen trip re-detect stands.
 *
 * There is no dedicated tenant-detail screen in the admin (the tenant list row IS
 * the detail surface — see the header note), so this sits in the status cell,
 * below the pill, matching the shipped Tenants layout rather than approximating a
 * detail screen that has no pixel source.
 *
 * Renders nothing for a plain open tenant that was never closed.
 */
function OffboardingState({ tenant }: { tenant: AdminTenant }) {
  const { t } = useTranslation();
  const closed = tenant.closedAt !== null;
  const pause = tenant.detectionPause ?? null;
  if (!closed && !pause) return null;

  const closedLine =
    closed && tenant.closedAt !== null
      ? (() => {
          const days = Math.floor((Date.now() - Date.parse(tenant.closedAt)) / 86_400_000);
          return days <= 0
            ? t('tenants.closedToday')
            : t('tenants.closedDaysAgo', { count: days });
        })()
      : null;

  const detectionLine = !pause
    ? null
    : pause.resumedAt === null
      ? // Still paused — the tenant is closed and its devices' detection is stopped.
        t('tenants.detectionPaused', { at: new Date(pause.pausedAt).toLocaleDateString() })
      : pause.backfilledAt === null
        ? // Reopened; the closed-window trip re-detect has not finished (or failed).
          t('tenants.reDetecting')
        : // Reopened and the closed-window trips have been re-detected.
          t('tenants.reDetected', { count: pause.backfillTrips ?? 0 });

  return (
    <div
      data-testid="offboarding-state"
      className="mt-1 space-y-0.5 text-[10.5px] leading-snug text-raad-muted"
    >
      {closedLine ? <p data-testid="closed-days">{closedLine}</p> : null}
      {detectionLine ? <p data-testid="detection-state">{detectionLine}</p> : null}
    </div>
  );
}

/** The artboard's leading badge: the vehicle count, then the name. */
function VehicleBadge({ count }: { count: number }) {
  return (
    <span className="inline-flex size-7 flex-none items-center justify-center rounded-lg bg-raad-primary/15 text-[12px] font-semibold text-raad-accent num">
      {count}
    </span>
  );
}

/** The props shared by the table row and the mobile card — one data source,
 *  two render branches (slice 84b). Role-gated handlers are `null` for an
 *  `admin_staff`, so those controls are never rendered (absence over disabled). */
type TenantActionProps = {
  tenant: AdminTenant;
  onOpen: (t: AdminTenant) => void;
  onAddVehicle: (t: AdminTenant) => void;
  onResetPassword: ((t: AdminTenant) => void) | null;
  onEditLogin: ((t: AdminTenant) => void) | null;
  onEditName: ((t: AdminTenant) => void) | null;
  onResetCommandPin: ((t: AdminTenant) => void) | null;
  onRaadSecure: ((t: AdminTenant) => void) | null;
  onCloseAccount: ((t: AdminTenant) => void) | null;
  onReopen: ((t: AdminTenant) => void) | null;
  onReleaseFleet: ((t: AdminTenant) => void) | null;
  opening: boolean;
};

/**
 * THE ACTION SET — ONE SOURCE for the table row AND the mobile card (slice 84b).
 *
 * Which buttons appear, and their role-gating, lived only in `TenantRow` before;
 * duplicating them into a card would be the exact drift CLAUDE.md warns about (a
 * super-admin-only control that leaks because the second copy forgot the gate).
 * So the set is defined once here and both render branches use it. Buttons are
 * `min-h-11` (44px) on a phone — where the CARD is the visible branch — and
 * `md:min-h-9` from `md`, where the TABLE is. The wrap is `flex flex-wrap gap-2`
 * for both (the pre-84b inline `ms-2` chain is gone; gap-2 is the same 8px).
 */
function TenantActions({
  tenant,
  onOpen,
  onAddVehicle,
  onResetPassword,
  onEditLogin,
  onEditName,
  onResetCommandPin,
  onRaadSecure,
  onCloseAccount,
  onReopen,
  onReleaseFleet,
  opening,
}: TenantActionProps) {
  const { t } = useTranslation();
  const closed = tenant.closedAt !== null;
  const BTN = 'min-h-11 md:min-h-9 flex items-center rounded-lg border px-3 text-[12.5px] font-semibold transition-colors';
  return (
    <div className="flex flex-wrap items-center gap-2">
      {closed ? (
        <>
          {/* Reopen is the undo of a mistake — recovery must not be harder than
              the action it recovers from. Super-admin only. */}
          {onReopen ? (
            <button
              type="button"
              onClick={() => onReopen(tenant)}
              className={`${BTN} border-raad-primary/40 text-raad-accent hover:bg-raad-primary/10`}
            >
              {t('offboard.reopen.action')}
            </button>
          ) : null}
          {/* Release fleet returns physical hardware to stock and can lose data
              (the export trap) — only reachable AFTER a close, super-admin only. */}
          {onReleaseFleet ? (
            <button
              type="button"
              onClick={() => onReleaseFleet(tenant)}
              className={`${BTN} border-raad-error/40 text-raad-error hover:bg-raad-error/10`}
            >
              {t('offboard.release.action')}
            </button>
          ) : null}
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => onOpen(tenant)}
            disabled={opening}
            title={t('tenants.readOnlyNote', { name: tenant.name })}
            className={`${BTN} border-raad-primary/40 text-raad-accent hover:bg-raad-primary/10 disabled:opacity-50`}
          >
            {opening ? t('tenants.opening') : t('tenants.openPortal')}
          </button>
          {/* Slice 18. It appears now, and only now, because there is something
              for it to POST to — the rule this project keeps re-learning. */}
          <button
            type="button"
            onClick={() => onAddVehicle(tenant)}
            className={`${BTN} border-raad-border hover:bg-raad-primary/10`}
          >
            {t('provision.addVehicle')}
          </button>
          {/* Slice 17x. Super-admin only, and ABSENT (not disabled) for anyone
              else — a dangling control that 403s on click is exactly what this
              codebase rules out. Resetting a customer's credential is the strongest
              impersonation-adjacent write in the product, so it matches the
              `requireSuperAdmin` gate on the route and the Staff nav gate. */}
          {onResetPassword ? (
            <button
              type="button"
              onClick={() => onResetPassword(tenant)}
              className={`${BTN} border-raad-border hover:bg-raad-primary/10`}
            >
              {t('resetPassword.action')}
            </button>
          ) : null}
          {/* Slice 52. Change the number a tenant user signs in with — the fix
              for an owner provisioned with a non-E.164 phone who therefore
              cannot log in. Super-admin only, matching the route's
              `requireSuperAdmin` gate; absent (not disabled) otherwise. */}
          {onEditLogin ? (
            <button
              type="button"
              onClick={() => onEditLogin(tenant)}
              className={`${BTN} border-raad-border hover:bg-raad-primary/10`}
            >
              {t('editLogin.action')}
            </button>
          ) : null}
          {/* Slice 55. Rename the customer-facing account name. Super-admin only,
              matching the route's `requireSuperAdmin` gate; absent (not disabled)
              otherwise. */}
          {onEditName ? (
            <button
              type="button"
              onClick={() => onEditName(tenant)}
              className={`${BTN} border-raad-border hover:bg-raad-primary/10`}
            >
              {t('editAccountName.action')}
            </button>
          ) : null}
          {/* Slice 14c. Super-admin only, matching the route's
              `requireSuperAdmin` gate — this CLEARS a customer's command PIN so
              they can set a new one; nobody, Raad included, can read the old one.
              Absent (not disabled) for anyone else. */}
          {onResetCommandPin ? (
            <button
              type="button"
              onClick={() => onResetCommandPin(tenant)}
              className={`${BTN} border-raad-border hover:bg-raad-primary/10`}
            >
              {t('commandPin.action')}
            </button>
          ) : null}
          {/* Slice 14d. The switch that turns Raad Secure on for a tenant —
              the thing that had no writer outside a psql prompt. Super-admin
              only, absent (not disabled) otherwise. The LABEL renders the
              tenant's REAL current state (`raadSecureEnabled` off the row), so
              the screen answers "is it on?" at a glance and the dialog only
              flips it. Turning it on makes customer-app controls appear; it
              does not make any vehicle blockable on its own. */}
          {onRaadSecure ? (
            <button
              type="button"
              onClick={() => onRaadSecure(tenant)}
              className={[
                BTN,
                tenant.raadSecureEnabled
                  ? 'border-raad-primary/40 text-raad-accent hover:bg-raad-primary/10'
                  : 'border-raad-border hover:bg-raad-primary/10',
              ].join(' ')}
            >
              {tenant.raadSecureEnabled
                ? t('raadSecure.badge.on')
                : t('raadSecure.badge.off')}
            </button>
          ) : null}
          {/* Slice 27. Close blocks access and is reversible; super-admin only,
              matching the route's `requireSuperAdmin` gate. Releasing hardware is
              a SEPARATE action that only appears once the account is closed. */}
          {onCloseAccount ? (
            <button
              type="button"
              onClick={() => onCloseAccount(tenant)}
              className={`${BTN} border-raad-error/40 text-raad-error hover:bg-raad-error/10`}
            >
              {t('offboard.close.action')}
            </button>
          ) : null}
        </>
      )}
    </div>
  );
}

/** The status pill + offboarding line — shared by the row and the card. */
function TenantStatus({ tenant }: { tenant: AdminTenant }) {
  const { t } = useTranslation();
  const closed = tenant.closedAt !== null;
  return (
    <>
      {closed ? (
        <span
          title={t('tenants.closedSince', { at: new Date(tenant.closedAt!).toLocaleDateString() })}
          className="inline-flex rounded-md border border-raad-border bg-raad-muted/15 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide text-raad-muted"
        >
          {t('tenants.status.closed')}
        </span>
      ) : (
        <span
          className={[
            'inline-flex rounded-md border px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wide',
            STATUS_TONE[tenant.status],
          ].join(' ')}
        >
          {t(`tenants.status.${tenant.status}`)}
        </span>
      )}
      {/* Slice 11b — read-only "Closed N days ago" + reopen re-detect state. */}
      <OffboardingState tenant={tenant} />
    </>
  );
}

function TenantRow(props: TenantActionProps) {
  const { t } = useTranslation();
  const { tenant } = props;
  // A closed account is a terminal state: the customer left. It dominates the
  // billing status (active/past_due/suspended), so the row reads as CLOSED, and
  // the live actions (open portal, add vehicle, reset) are replaced by the two
  // offboarding actions — a closed tenant cannot be impersonated (the API refuses
  // it 401), so a portal button that would only error is not shown.
  const closed = tenant.closedAt !== null;
  return (
    <tr className="border-b border-raad-border last:border-b-0">
      <td className="py-3 pr-4">
        <span className="flex items-center gap-2.5">
          <VehicleBadge count={tenant.vehicleCount} />
          <span className={['font-medium', closed ? 'text-raad-muted' : ''].join(' ')}>
            {tenant.name}
          </span>
        </span>
      </td>
      <td className="py-3 pr-4">{t(`tenants.plan.${tenant.plan}`)}</td>
      <td className="py-3 pr-4 text-right">
        <Money cents={tenant.monthlyRateCents} />
      </td>
      <td className="py-3 pr-4">
        <TenantStatus tenant={tenant} />
      </td>
      <td className="py-3">
        <TenantActions {...props} />
      </td>
    </tr>
  );
}

/**
 * The mobile card (< md) — slice 84b. Same data and the same `TenantActions` set
 * as the table row; the two just lay out differently. `data-testid` is what the
 * mobile gate keys the reflow assertion on.
 */
function TenantCard(props: TenantActionProps) {
  const { t } = useTranslation();
  const { tenant } = props;
  const closed = tenant.closedAt !== null;
  return (
    <div
      data-testid="tenant-card"
      className="rounded-panel border border-raad-border bg-raad-panel p-4"
    >
      <div className="flex items-center gap-2.5">
        <VehicleBadge count={tenant.vehicleCount} />
        <span className={['font-medium', closed ? 'text-raad-muted' : ''].join(' ')}>
          {tenant.name}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12.5px] text-raad-muted">
        <span>{t(`tenants.plan.${tenant.plan}`)}</span>
        <span aria-hidden>·</span>
        <Money cents={tenant.monthlyRateCents} />
        <span aria-hidden>·</span>
        <span className="inline-flex flex-wrap items-center gap-1">
          <TenantStatus tenant={tenant} />
        </span>
      </div>
      <div className="mt-3">
        <TenantActions {...props} />
      </div>
    </div>
  );
}

export function Tenants() {
  const { t } = useTranslation();
  const q = useTenants();
  const { state } = useStaffAuth();
  const isMobile = useIsMobile();
  // 🔴 The reset action exists for a super-admin only, matching the route's
  // `requireSuperAdmin` gate and the Staff nav gate. `null` when the viewer is
  // an admin_staff, so the button is never rendered — absence over disabled.
  const isSuperAdmin =
    state.status === 'authenticated' && state.user.role === 'super_admin';
  const [search, setSearch] = useState('');
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openError, setOpenError] = useState(false);
  const [provisioning, setProvisioning] = useState<AdminTenant | null>(null);
  const [creatingTenant, setCreatingTenant] = useState(false);
  const [resetting, setResetting] = useState<AdminTenant | null>(null);
  const [editingLogin, setEditingLogin] = useState<AdminTenant | null>(null);
  const [editingName, setEditingName] = useState<AdminTenant | null>(null);
  const [resettingPin, setResettingPin] = useState<AdminTenant | null>(null);
  const [securing, setSecuring] = useState<AdminTenant | null>(null);
  const [closingAccount, setClosingAccount] = useState<AdminTenant | null>(null);
  const [reopening, setReopening] = useState<AdminTenant | null>(null);
  const [releasing, setReleasing] = useState<AdminTenant | null>(null);

  const data = q.data as Partial<typeof q.data> | undefined;
  const rows = Array.isArray(data?.tenants) ? data.tenants : null;

  const filtered = useMemo(() => {
    if (rows === null) return [];
    const needle = search.trim().toLowerCase();
    if (needle === '') return rows;
    return rows.filter((tn) => tn.name.toLowerCase().includes(needle));
  }, [rows, search]);

  async function openPortal(tenant: AdminTenant) {
    setOpeningId(tenant.id);
    setOpenError(false);
    try {
      const session = await startImpersonation(tenant.id);
      // Handed over in the FRAGMENT, never the query string: a fragment is not
      // sent to the server, so a live credential that reads this customer's
      // fleet never lands in an access log, a Referer header, or a proxy.
      const url = `https://app.raadtelematics.com/#impersonate=${encodeURIComponent(session.accessToken)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      setOpenError(true);
    } finally {
      setOpeningId(null);
    }
  }

  if (q.isPending) return <p className="text-sm text-raad-muted">{t('tenants.loading')}</p>;
  if (q.isError) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('tenants.error')}
      </p>
    );
  }

  // Defensive, for the reason slice 24b earned: an unexpected response shape
  // must not throw during render and take the portal down. Treated as an ERROR,
  // never an empty list — "no tenants" would be a claim about the business.
  if (rows === null) {
    return (
      <p role="alert" className="text-sm text-raad-error">
        {t('tenants.error')}
      </p>
    );
  }

  const totalMonthlyCents =
    typeof data?.totalMonthlyCents === 'number' ? data.totalMonthlyCents : 0;
  const activeCount = rows.filter((tn) => tn.status === 'active').length;
  const vehicleTotal = rows.reduce((sum, tn) => sum + tn.vehicleCount, 0);

  return (
    <section aria-labelledby="tenants-heading">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h1 id="tenants-heading" className="font-display text-xl font-bold tracking-tight">
          {t('tenants.title')}
        </h1>
        {/* Slice 18b. Forbidden by a test until now, because until now there
            was nowhere to POST it and the pilot's tenant was made in SQL. */}
        <button
          type="button"
          onClick={() => setCreatingTenant(true)}
          className="order-last ms-auto min-h-9 rounded-lg bg-raad-primary px-3 text-[12.5px] font-semibold text-white"
        >
          {t('newTenant.newTenant')}
        </button>
        <p className="text-sm text-raad-muted">
          {t('tenants.activeVehicles', { active: activeCount, vehicles: vehicleTotal })}
          {' · '}
          <span className="num">{`$${(totalMonthlyCents / 100).toFixed(2)}`}</span>{' '}
          {t('tenants.perMonth')}
        </p>
      </div>

      <label className="mt-4 block max-w-xs">
        <span className="sr-only">{t('tenants.search')}</span>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('tenants.search')}
          className="min-h-10 w-full rounded-lg border border-raad-border bg-raad-panel px-3 text-sm text-raad-text"
        />
      </label>

      {openError ? (
        <p role="alert" className="mt-3 text-[12.5px] text-raad-error">
          {t('tenants.openFailed')}
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('tenants.empty')}</p>
      ) : filtered.length === 0 ? (
        <p className="mt-4 text-sm text-raad-muted">{t('tenants.searchNone', { q: search })}</p>
      ) : isMobile ? (
        /* Mobile (< md): stacked cards — slice 84b. Same rows, same actions. A
           matchMedia single branch (not CSS `hidden`), so only ONE copy of each
           row is in the DOM — see `useIsMobile`. */
        <div className="mt-4 space-y-3">
          {filtered.map((tn) => (
            <TenantCard
              key={tn.id}
              tenant={tn}
              onOpen={(x) => void openPortal(x)}
              onAddVehicle={setProvisioning}
              onResetPassword={isSuperAdmin ? setResetting : null}
              onEditLogin={isSuperAdmin ? setEditingLogin : null}
              onEditName={isSuperAdmin ? setEditingName : null}
              onResetCommandPin={isSuperAdmin ? setResettingPin : null}
              onRaadSecure={isSuperAdmin ? setSecuring : null}
              onCloseAccount={isSuperAdmin ? setClosingAccount : null}
              onReopen={isSuperAdmin ? setReopening : null}
              onReleaseFleet={isSuperAdmin ? setReleasing : null}
              opening={openingId === tn.id}
            />
          ))}
        </div>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-panel border border-raad-border bg-raad-panel px-4">
          <table className="w-full min-w-[38rem] text-sm">
            <thead>
              <tr className="border-b border-raad-border text-[11px] uppercase tracking-wide text-raad-muted">
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('tenants.col.name')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('tenants.col.plan')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-right font-semibold">
                  {t('tenants.col.rate')}
                </th>
                <th scope="col" className="py-2.5 pr-4 text-left font-semibold">
                  {t('tenants.col.status')}
                </th>
                <th scope="col" className="py-2.5 text-left font-semibold">
                  {t('tenants.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tn) => (
                <TenantRow
                  key={tn.id}
                  tenant={tn}
                  onOpen={(x) => void openPortal(x)}
                  onAddVehicle={setProvisioning}
                  onResetPassword={isSuperAdmin ? setResetting : null}
                  onEditLogin={isSuperAdmin ? setEditingLogin : null}
                  onEditName={isSuperAdmin ? setEditingName : null}
                  onResetCommandPin={isSuperAdmin ? setResettingPin : null}
                  onRaadSecure={isSuperAdmin ? setSecuring : null}
                  onCloseAccount={isSuperAdmin ? setClosingAccount : null}
                  onReopen={isSuperAdmin ? setReopening : null}
                  onReleaseFleet={isSuperAdmin ? setReleasing : null}
                  opening={openingId === tn.id}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {provisioning ? (
        <ProvisionWizard tenant={provisioning} onClose={() => setProvisioning(null)} />
      ) : null}
      {creatingTenant ? <NewTenantDialog onClose={() => setCreatingTenant(false)} /> : null}
      {resetting ? (
        <ResetPasswordDialog tenant={resetting} onClose={() => setResetting(null)} />
      ) : null}
      {editingLogin ? (
        <EditLoginDialog tenant={editingLogin} onClose={() => setEditingLogin(null)} />
      ) : null}
      {editingName ? (
        <EditAccountNameDialog tenant={editingName} onClose={() => setEditingName(null)} />
      ) : null}
      {resettingPin ? (
        <CommandPinResetDialog tenant={resettingPin} onClose={() => setResettingPin(null)} />
      ) : null}
      {securing ? (
        <RaadSecureDialog tenant={securing} onClose={() => setSecuring(null)} />
      ) : null}
      {closingAccount ? (
        <CloseAccountDialog tenant={closingAccount} onClose={() => setClosingAccount(null)} />
      ) : null}
      {reopening ? (
        <ReopenDialog tenant={reopening} onClose={() => setReopening(null)} />
      ) : null}
      {releasing ? (
        <ReleaseFleetDialog tenant={releasing} onClose={() => setReleasing(null)} />
      ) : null}
    </section>
  );
}

export default Tenants;
